import React, { useState, useRef, useEffect, useCallback } from 'react'
import ReactDOM from 'react-dom'

export interface DropdownActionItem {
  key: string
  label: string
  icon?: React.ReactNode
  onClick: () => void
  variant?: 'default' | 'warning' | 'danger' | 'success'
  divider?: boolean
  hidden?: boolean
}

interface Props {
  label: string
  items: DropdownActionItem[]
  buttonClassName?: string
  menuClassName?: string
  align?: 'end' | 'start'
}

const DropdownActions: React.FC<Props> = ({
  label,
  items,
  buttonClassName = 'btn btn-sm btn-light btn-active-light-primary',
  menuClassName = 'menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-200px p-3',
  align = 'end',
}) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const updatePosition = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    const menuWidth = menuRef.current?.offsetWidth ?? 200
    const menuHeight = menuRef.current?.offsetHeight ?? 200

    let top = rect.bottom + 4
    let left = align === 'end'
      ? rect.right - menuWidth
      : rect.left

    if (top + menuHeight > window.innerHeight - 8) {
      top = rect.top - menuHeight - 4
    }

    if (left < 8) left = 8
    if (left + menuWidth > window.innerWidth - 8) {
      left = window.innerWidth - menuWidth - 8
    }

    setPosition({ top, left })
  }, [align])

  const toggle = () => {
    if (!open) {
      updatePosition()
    }
    setOpen((prev) => !prev)
  }

  const close = () => setOpen(false)

  useEffect(() => {
    if (!open) return

    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current && !buttonRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        close()
      }
    }

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }

    const handleScroll = () => {
      updatePosition()
    }

    document.addEventListener('mousedown', handleClickOutside)
    document.addEventListener('keydown', handleEscape)
    window.addEventListener('scroll', handleScroll, true)
    window.addEventListener('resize', handleScroll)

    requestAnimationFrame(updatePosition)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleEscape)
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleScroll)
    }
  }, [open, updatePosition])

  const handleItemClick = (item: DropdownActionItem) => {
    close()
    item.onClick()
  }

  const variantClass = (variant?: string) => {
    switch (variant) {
      case 'warning': return 'text-warning'
      case 'danger': return 'text-danger'
      case 'success': return 'text-success'
      default: return ''
    }
  }

  const visibleItems = items.filter((item) => !item.hidden)

  return (
    <>
      <button
        ref={buttonRef}
        className={buttonClassName}
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle() }}
      >
        {label}
      </button>

      {open && position && ReactDOM.createPortal(
        <div
          ref={menuRef}
          className={menuClassName}
          style={{
            position: 'fixed',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1080,
            display: 'block',
          }}
        >
          {visibleItems.map((item, idx) => (
            <React.Fragment key={item.key}>
              {item.divider && idx > 0 && <div className="separator my-2"></div>}
              <div className="menu-item">
                <span
                  className={`menu-link px-3 ${variantClass(item.variant)}`}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleItemClick(item)
                  }}
                >
                  {item.icon && <span className="me-2">{item.icon}</span>}
                  {item.label}
                </span>
              </div>
            </React.Fragment>
          ))}
        </div>,
        document.body
      )}
    </>
  )
}

export default DropdownActions
